import { RouterProvider } from '@tanstack/react-router';
import { useMemo, useState } from 'react';

import { createAccountApi } from './api/client';
import { parseReturnOrigins } from './auth/returnOrigin';
import { unconfiguredAuth } from './auth/types';
import { useMsalAuthApi } from './auth/useMsalAuthApi';
import { env } from './env';
import { createAppRouter } from './router';

import type { EntraConfig } from './auth/msal';
import type { AuthApi } from './auth/types';
import type { QueryClient } from '@tanstack/react-query';

const RETURN_ORIGINS = parseReturnOrigins(env.VITE_RETURN_ORIGINS);

export interface AppProps {
  entra: EntraConfig | null;
  queryClient: QueryClient;
  /** The session in mock mode (`VITE_API_MOCK=1`); no tenant needed. */
  mockAuth?: AuthApi;
}

function RouterHost({ auth, queryClient }: { auth: AuthApi; queryClient: QueryClient }) {
  const api = useMemo(
    () => createAccountApi({ getAccessToken: () => auth.getAccessToken() }),
    [auth],
  );
  const [router] = useState(() =>
    createAppRouter({ auth, api, queryClient, returnOrigins: RETURN_ORIGINS }),
  );
  return (
    <RouterProvider
      router={router}
      context={{ auth, api, queryClient, returnOrigins: RETURN_ORIGINS }}
    />
  );
}

function MsalApp({ entra, queryClient }: { entra: EntraConfig; queryClient: QueryClient }) {
  const auth = useMsalAuthApi(entra);
  return <RouterHost auth={auth} queryClient={queryClient} />;
}

export function App({ entra, queryClient, mockAuth }: AppProps) {
  if (mockAuth) return <RouterHost auth={mockAuth} queryClient={queryClient} />;
  return entra ? (
    <MsalApp entra={entra} queryClient={queryClient} />
  ) : (
    <RouterHost auth={unconfiguredAuth} queryClient={queryClient} />
  );
}
